import {
  Datatable,
  DatatableBody,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
} from '@monksflow/monks-ui';
import type { BriefTemplate } from '@/entities/brief-template';
import { BriefTemplatesTableRow } from '../BriefTemplatesTableRow';
import { BriefTemplatesTableSkeletonRow } from './BriefTemplatesTableSkeletonRow';
import { useTranslation } from 'react-i18next';

interface BriefTemplatesTableProps {
  templates: BriefTemplate[];
  isLoading: boolean;
  /** The `brief_template_levels` flag: adds the Level column and the level-aware row menu. */
  levelsEnabled?: boolean;
}

const SKELETON_ROW_COUNT = 5;
const SKELETON_ROWS = Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => i);

/**
 * Pure-presentational. Empty/error/no-results UX is owned by the orchestrator.
 *
 * Migrated to monks-ui `Datatable*` directly. The
 * label-less actions column carries its accessible name via an inline
 * `<span className="sr-only">Actions</span>` inside the `<DatatableHead>`
 * (design D6) — the deliberate a11y gain the spec calls out.
 *
 * Per design D5: `aria-busy` is a COMPOSITE gate at the call site
 * (`isLoading && templates.length === 0`) so background refetches don't
 * announce a busy state on every render.
 *
 * Column widths are LOCAL to this table, not `TABLE_COL_WIDTH` (see
 * `@/shared/constants`) — a deliberate product decision to match this
 * table's own design rather than the narrower shared geometry
 * `ProjectsTable`/`EmailTemplatesTable` align to. That means the Name column
 * here does NOT line up with those tables' Name column; this table has no
 * balancing spacer for the same reason. Don't "fix" this by reapplying the
 * shared tokens without checking with product first.
 */
export function BriefTemplatesTable(props: BriefTemplatesTableProps) {
  const { t } = useTranslation('knowledge');

  const { templates, isLoading, levelsEnabled = false } = props;

  const isBusy = isLoading && templates.length === 0;
  const showSkeletons = isBusy;

  // Header divider = 1px to match the app's other list tables (see
  // ProjectsTable): the monks-ui `Datatable` primitive defaults `DatatableHead`
  // to `border-b-2` (2px); `[&_thead_th]:border-b` overrides the width to 1px.
  return (
    <Datatable aria-busy={isBusy || undefined} className="[&_thead_th]:border-b">
      <DatatableHeader>
        <DatatableRow>
          <DatatableHead scope="col">{t('knowledgeHub.briefTemplatesTable.name')}</DatatableHead>
          {levelsEnabled && (
            <DatatableHead scope="col" className="w-40">
              {t('knowledgeHub.briefTemplatesTable.level')}
            </DatatableHead>
          )}
          <DatatableHead scope="col" className="w-33">
            {t('knowledgeHub.briefTemplatesTable.status')}
          </DatatableHead>
          <DatatableHead scope="col" className="w-55">
            {t('knowledgeHub.briefTemplatesTable.createdBy')}
          </DatatableHead>
          <DatatableHead scope="col" className="w-45">
            {t('knowledgeHub.briefTemplatesTable.lastModified')}
          </DatatableHead>
          <DatatableHead scope="col" className="w-14">
            <span className="sr-only">{t('knowledgeHub.briefTemplatesTable.actions')}</span>
          </DatatableHead>
        </DatatableRow>
      </DatatableHeader>
      <DatatableBody>
        {showSkeletons
          ? SKELETON_ROWS.map((i) => (
              <BriefTemplatesTableSkeletonRow key={i} showLevel={levelsEnabled} />
            ))
          : templates.map((template) => (
              <BriefTemplatesTableRow
                key={template.id}
                template={template}
                levelsEnabled={levelsEnabled}
              />
            ))}
      </DatatableBody>
    </Datatable>
  );
}
